import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

export const SQFT_OPTIONS = [
  { value: "1000", label: "0 - 1,000 sq.ft" },
  { value: "3000", label: "1,001 - 3,000 sq.ft" },
  { value: "5000", label: "3,001 - 5,000 sq.ft" },
  { value: "10000", label: "5,001 - 10,000+ sq.ft" },
];

export function blocksFor(sqft) {
  if (!sqft || sqft <= 1000) return 0;
  if (sqft <= 3000) return 1;
  if (sqft <= 5000) return 2;
  if (sqft <= 10000) return 3;
  return 3;
}
const tierValueFor = (n) => {
  if (n <= 1000) return "1000";
  if (n <= 3000) return "3000";
  if (n <= 5000) return "5000";
  if (n <= 10000) return "10000";
  return "10000";
};
export const sqftLabelFor = (v) => SQFT_OPTIONS.find((o) => o.value === String(v))?.label || "";

/** Holds all state and handlers for the public booking wizard (Booking.js),
 * so each step's JSX can stay focused on rendering. */
export function useBookingForm() {
  const [catalog, setCatalog] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const [address, setAddress] = useState({ street: "", city: "", state: "", zip: "" });
  const [sqft, setSqft] = useState("");
  const [sqftLoading, setSqftLoading] = useState(false);
  const [sqftAuto, setSqftAuto] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const [services, setServices] = useState([]);
  const [vsOption, setVsOption] = useState("");
  const [twilightOption, setTwilightOption] = useState("");

  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [meeting, setMeeting] = useState({ contact_name: "", contact_phone: "", access: "", notes: "" });
  const [contact, setContact] = useState({ name: "", phone: "", email: "", company: "" });
  const [submissionId] = useState(() => window.crypto?.randomUUID?.() || `booking-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const [terms, setTerms] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const [quote, setQuote] = useState(null);

  useEffect(() => { api.get("/services").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const blocks = blocksFor(Number(sqft));
  const onsiteMinutes = useMemo(() => services.reduce((acc, sid) => {
    const s = (catalog?.services || []).find((x) => x.id === sid);
    return acc + (s?.onsite_minutes || 0);
  }, 0), [services, catalog]);
  const hasOnsite = onsiteMinutes > 0;

  const grouped = useMemo(() => {
    const order = [], map = {};
    (catalog?.services || []).forEach((s) => {
      if (!map[s.category]) { map[s.category] = []; order.push(s.category); }
      map[s.category].push(s);
    });
    return order.map((c) => ({ category: c, items: map[c] }));
  }, [catalog]);

  useEffect(() => {
    const payload = {
      services, sqft: Number(sqft) || 0,
      virtual_staging: vsOption ? { option: vsOption } : null,
      twilight_option: twilightOption || null,
      coupon_code: appliedCoupon ? appliedCoupon.code : null,
    };
    if (!services.length && !vsOption && !twilightOption) { setQuote(null); return; }
    api.post("/pricing/quote", payload).then((r) => setQuote(r.data)).catch(() => {});
  }, [services, sqft, vsOption, twilightOption, appliedCoupon]);

  const toggleService = (id) => setServices((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const lookupSqft = async () => {
    if (!address.street || !address.city || !address.state) return;
    if (sqft && !sqftAuto) return;
    setSqftLoading(true);
    try {
      const r = await api.post("/property/lookup", { address });
      if (r.data.found && r.data.square_footage) {
        setSqft(tierValueFor(Number(r.data.square_footage)));
        setSqftAuto(true);
      }
    } catch (e) { /* lookup failed; sqft stays as entered */ } finally { setSqftLoading(false); }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const r = await api.post("/coupons/validate", { code: couponCode });
      setAppliedCoupon(r.data);
      toast.success(`Coupon ${r.data.code} applied`);
    } catch (err) {
      setAppliedCoupon(null);
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const loadSlots = async (d) => {
    if (!d) return;
    setSlotsLoading(true);
    setTime("");
    try {
      const r = await api.post("/scheduling/slots", { date: d, onsite_minutes: onsiteMinutes || 60, services, address });
      setSlots(r.data.slots);
    } catch (e) { setSlots([]); } finally { setSlotsLoading(false); }
  };

  const cartCount = services.length + (vsOption ? 1 : 0) + (twilightOption ? 1 : 0);

  const canNext = () => {
    if (step === 1) return address.street && address.city && address.state && address.zip && sqft;
    if (step === 2) return cartCount > 0;
    if (step === 3) return !hasOnsite || (date && time);
    if (step === 4) return true;
    return false;
  };

  const next = () => {
    if (!canNext()) { toast.error("Please complete the required fields"); return; }
    if (step === 2 && !hasOnsite) { setStep(4); return; }
    setStep((s) => Math.min(s + 1, 5));
  };
  const back = () => {
    if (step === 4 && !hasOnsite) { setStep(2); return; }
    setStep((s) => Math.max(s - 1, 1));
  };

  const submit = async () => {
    if (!terms || !confirm) { toast.error("Please agree to the terms and confirm your order"); return; }
    if (!contact.name || !contact.email || !contact.phone) { toast.error("Please complete your contact details"); return; }
    setSubmitting(true);
    try {
      const payload = {
        address, services, sqft: Number(sqft) || 0,
        virtual_staging: vsOption ? { option: vsOption } : null,
        twilight_option: twilightOption || null,
        coupon_code: appliedCoupon ? appliedCoupon.code : null,
        scheduled_date: hasOnsite ? date : null, scheduled_time: hasOnsite ? time : null,
        meeting, contact, terms_agreed: terms, submission_id: submissionId,
      };
      const r = await api.post("/bookings", payload);
      setDone(r.data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return {
    catalog, step, setStep, submitting, done,
    address, setAddress, sqft, setSqft, sqftLoading, sqftAuto, setSqftAuto, couponCode, setCouponCode, appliedCoupon,
    services, vsOption, setVsOption, twilightOption, setTwilightOption,
    date, setDate, time, setTime, slots, slotsLoading,
    meeting, setMeeting, contact, setContact, terms, setTerms, confirm, setConfirm,
    quote, blocks, onsiteMinutes, hasOnsite, grouped,
    toggleService, lookupSqft, applyCoupon, loadSlots, canNext, next, back, submit,
  };
}
